/* uploadsqueue.jsx — ORG-04 "Profile sources" screen.
   Two halves on one screen, role-gated by the SHARED matrix:
     • submit (ROLE_MATRIX.upload): a dev/sme uploads a variant-profile source
       (1 .pdf design + 1 shipped .html + images) FOR an assigned client; after
       submit a "pending SME approval" banner carries the state.
     • review queue (ROLE_MATRIX.upload_decision): SMEs see ONLY their assigned
       clients' pending rows (the server filters in SQL); approve / revoke (note
       required) with expected_digest so a stale decision 409s instead of landing.
   Authority is the SERVER's (requireRole + the client-scope guard + the ledger);
   this screen is presentation, kb.jsx-pattern. */

const { useState: useUqState, useEffect: useUqEffect } = React;

function fmtSize(n) {
  if (n > 1024 * 1024) return (n / (1024 * 1024)).toFixed(1) + ' MB';
  if (n > 1024) return Math.round(n / 1024) + ' KB';
  return n + ' B';
}

function ProfileSources() {
  const v = (window.RBAC && window.RBAC.viewer && window.RBAC.viewer()) || null;
  const canSubmit = window.RBAC ? window.RBAC.requireRole('upload', v) : true;
  const canDecide = window.RBAC ? window.RBAC.requireRole('upload_decision', v) : false;
  const role = (window.RBAC && window.RBAC.roleForContext && window.RBAC.roleForContext(v)) || null;
  const isClientScoped = role === 'sme' || role === 'dev';

  // client options: sme/dev pick from their REAL assignments; managers from the KB list
  const myAssigned = ((window.MAV.me && window.MAV.me.assignments) || []);
  const clientOpts = isClientScoped
    ? myAssigned.map((a) => ({ slug: a.clientSlug, name: a.clientName || a.clientSlug }))
    : (window.MAV.clients || []).map((c) => ({ slug: c.slug, name: c.name || c.slug }));

  const [clientSlug, setClientSlug] = useUqState(clientOpts[0] ? clientOpts[0].slug : '');
  const [files, setFiles] = useUqState([]);
  const [busy, setBusy] = useUqState(false);
  const [err, setErr] = useUqState(null);
  const [submitted, setSubmitted] = useUqState(null);   // {upload_id, state, client_slug}

  const submit = async () => {
    if (busy || !files.length || !clientSlug) return;
    setBusy(true); setErr(null);
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    fd.append('kind', 'variant_profile_source');
    fd.append('client_slug', clientSlug);
    try {
      const r = await window.GooseAPI.uploadFiles(fd);
      setSubmitted(r); setFiles([]);
    } catch (e) { setErr((e && e.data && e.data.error) || e.message || 'Upload failed.'); }
    setBusy(false);
  };

  return (
    <div className="members-admin">
      <div style={{ marginBottom: 14 }}>
        <div className="sec-title">Profile sources</div>
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
          Variant-profile source files (design PDF + shipped HTML + images). An SME must approve a source before a build can use it.
        </div>
      </div>

      {canSubmit && (
        <div className="card" style={{ padding: 14, marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Submit a source</div>
          {clientOpts.length === 0 ? (
            <p className="muted" style={{ fontSize: 12.5 }}>
              {isClientScoped ? 'No clients are assigned to you yet — ask your team lead.' : 'No clients in the KB yet.'}
            </p>
          ) : (
            <React.Fragment>
              <div className="row wrap" style={{ gap: 10, alignItems: 'center' }}>
                <select className="mb-select" value={clientSlug} onChange={(e) => setClientSlug(e.target.value)}>
                  {clientOpts.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
                <input type="file" multiple accept=".pdf,.html,.htm,.png,.jpg,.jpeg,.gif"
                  onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                <button className="btn btn-primary" disabled={busy || !files.length} onClick={submit}>
                  {busy ? <span className="spinner" /> : <I.check size={14} />} Upload for approval
                </button>
              </div>
              <p className="muted" style={{ fontSize: 11.5, marginTop: 8 }}>
                Exactly one .pdf (the design) + one .html (the dev's shipped reference) + optional images.
              </p>
            </React.Fragment>
          )}
          {err && <p style={{ color: 'var(--fail)', fontSize: 12.5, marginTop: 8 }} role="alert">{err}</p>}
          {submitted && (
            <div className="row" style={{ gap: 8, alignItems: 'center', marginTop: 10 }}>
              <span className="mb-status on">Submitted</span>
              <span className="muted" style={{ fontSize: 12.5 }}>
                <b>{submitted.upload_id}</b> for <b>{submitted.client_slug}</b> — pending SME approval. You'll be able to build with it once approved.
              </span>
            </div>
          )}
        </div>
      )}

      {canDecide && <PendingQueue isClientScoped={isClientScoped} />}
    </div>
  );
}

function PendingQueue({ isClientScoped }) {
  const [rows, setRows] = useUqState(null);
  const [err, setErr] = useUqState(null);
  const [busy, setBusy] = useUqState(null);      // upload_id mid-decision
  const [noteFor, setNoteFor] = useUqState(null); // upload_id collecting a revoke note
  const [note, setNote] = useUqState('');

  const load = () => window.GooseAPI.uploadsPending()
    .then((d) => { setRows(d.uploads || []); setErr(null); })
    .catch((e) => setErr(e.message));
  useUqEffect(() => { load(); }, []);

  const decide = async (u, decision, noteText) => {
    setBusy(u.upload_id); setErr(null);
    try {
      await window.GooseAPI.uploadDecide(u.upload_id, {
        decision, note: noteText || undefined, expected_digest: u.files_digest,
      });
      setNoteFor(null); setNote('');
      await load();
    } catch (e) { setErr((e && e.data && e.data.error) || e.message); }
    setBusy(null);
  };

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Approval queue</div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
        {isClientScoped ? 'Sources for your assigned clients.' : 'All pending sources in this workspace.'}
      </div>
      {err && <div className="muted" style={{ color: 'var(--fail)', fontSize: 12.5, marginBottom: 8 }} role="alert">{err}</div>}
      {rows === null
        ? <div className="muted" style={{ fontSize: 12.5 }}>Loading…</div>
        : rows.length === 0
          ? <div className="muted" style={{ fontSize: 12.5 }}>Nothing pending. 🎉</div>
          : rows.map((u) => (
            <div key={u.upload_id} className="card" style={{ padding: 12, marginBottom: 10 }}>
              <div className="row wrap between" style={{ gap: 8, alignItems: 'center' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {u.client_name || u.client_slug || '(no client)'}
                    <span className="muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
                      {u.kind === 'variant_profile_source' ? 'Variant profile source' : 'Build brief'} · by {u.uploader_name || u.uploader_id}
                    </span>
                  </div>
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
                    {(u.files || []).map((f) => `${f.name} (${fmtSize(f.size)})`).join(' · ')}
                  </div>
                </div>
                <div className="row" style={{ gap: 8, flexShrink: 0 }}>
                  <button className="btn btn-primary" disabled={busy === u.upload_id}
                    onClick={() => decide(u, 'approve')}>
                    {busy === u.upload_id ? <span className="spinner" /> : <I.check size={14} />} Approve
                  </button>
                  <button className="btn btn-quiet" disabled={busy === u.upload_id}
                    onClick={() => { setNoteFor(noteFor === u.upload_id ? null : u.upload_id); setNote(''); }}>
                    Revoke…
                  </button>
                </div>
              </div>
              {noteFor === u.upload_id && (
                <div className="row" style={{ gap: 8, marginTop: 10, alignItems: 'center' }}>
                  <input className="input" style={{ flex: 1 }} placeholder="Why is this revoked? (required — the uploader sees this)"
                    value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
                  <button className="btn btn-quiet" disabled={!note.trim() || busy === u.upload_id}
                    onClick={() => decide(u, 'revoke', note.trim())}>
                    Confirm revoke
                  </button>
                </div>
              )}
            </div>
          ))}
    </div>
  );
}

Object.assign(window, { ProfileSources });
